import React, { useState, useEffect, useCallback } from "react";
import { Card, CardContent, Avatar, Typography, LinearProgress, Box, Stack } from "@mui/material";
import Grid2 from "@mui/material/Unstable_Grid2/Grid2";
import { makeStyles } from "@material-ui/core/styles";
import { useHistory } from "react-router-dom";
import SendIcon from "@material-ui/icons/Send";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import MessageIcon from "@material-ui/icons/Message";
import api from "../../services/api";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  overline: {
    fontSize: "0.9rem",
    fontWeight: 700,
    color: theme.palette.text.secondary,
    letterSpacing: "0.5px",
    lineHeight: 2.5,
    textTransform: "uppercase",
    fontFamily: "'Plus Jakarta Sans', sans-serif'",
  },
  h4: {
    fontFamily: "'Plus Jakarta Sans', sans-serif'",
    fontWeight: 500,
    fontSize: "2rem",
    lineHeight: 1,
    color: theme.palette.text.primary,
  },
  cardContent: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  metricRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  sectionTitle: {
    fontSize: "1.1rem",
    fontWeight: 600,
    color: theme.palette.primary.main,
    marginBottom: theme.spacing(1),
    fontFamily: "'Plus Jakarta Sans', sans-serif'",
  },
}));

const CampaignMetaMetrics = ({ initialDate, finalDate }) => {
  const classes = useStyles();
  const history = useHistory();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    geral: { totalAgendado: 0, totalEntregue: 0, totalRespostas: 0, taxaResposta: 0 },
    preventivo: { totalAgendado: 0, totalEntregue: 0, totalRespostas: 0, taxaResposta: 0 },
    preventivo3d: { totalAgendado: 0, totalEntregue: 0, totalRespostas: 0, taxaResposta: 0 },
    quebraAcordo: { totalAgendado: 0, totalEntregue: 0, totalRespostas: 0, taxaResposta: 0 },
    cadastroNovo: { totalAgendado: 0, totalEntregue: 0, totalRespostas: 0, taxaResposta: 0 },
  });

  const fetchStats = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (initialDate && finalDate) {
        params.initialDate = initialDate;
        params.finalDate = finalDate;
      }

      console.log('🔍 [CampaignMetaMetrics] Buscando stats com params:', params);
      console.log('🔍 [CampaignMetaMetrics] initialDate recebido:', initialDate);
      console.log('🔍 [CampaignMetaMetrics] finalDate recebido:', finalDate);

      const { data } = await api.get("/campaigns-meta/dashboard-stats", { params });
      console.log('📊 [CampaignMetaMetrics] Stats retornados:', data);
      setStats(data);
    } catch (error) {
      console.error("Erro ao buscar estatísticas de campanhas META:", error);
      
      // Error handling melhorado (fix: erro genérico)
      const errorMsg = error.response?.data?.error || "Erro ao buscar estatísticas de campanhas META";
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  }, [initialDate, finalDate]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const MetricCard = ({ title, icon, bgColor, stats, subtitle, templateName }) => (
    <Grid2 xs={12} sm={6} md={4} lg={2.4}>
      <Card sx={{ height: '100%' }}>
        <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          <Stack 
            direction="row" 
            justifyContent="space-between" 
            alignItems="flex-start"
            spacing={2}
            sx={{ mb: 1 }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography 
                className={classes.overline} 
                color="text.secondary"
                sx={{ 
                  fontSize: { xs: '0.75rem', sm: '0.8rem', md: '0.9rem' },
                  lineHeight: 1.5,
                  wordBreak: 'break-word'
                }}
              >
                {title}
              </Typography>
              {subtitle && (
                <Typography 
                  variant="caption" 
                  color="text.secondary" 
                  sx={{ 
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                    display: 'block',
                    mt: 0.5,
                    wordBreak: 'break-word'
                  }}
                >
                  {subtitle}
                </Typography>
              )}
            </Box>
            <Avatar
              onClick={() => {
                if (templateName) {
                  history.push(`/tickets?templateName=${templateName}`);
                }
              }}
              sx={{
                backgroundColor: bgColor,
                height: { xs: 48, sm: 52, md: 56 },
                width: { xs: 48, sm: 52, md: 56 },
                flexShrink: 0,
                cursor: templateName ? 'pointer' : 'default',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': templateName ? {
                  transform: 'scale(1.05)',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                } : {},
                '& svg': {
                  fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' }
                }
              }}
            >
              {icon}
            </Avatar>
          </Stack>
          
          <Stack spacing={0.5} sx={{ mt: 'auto' }}>
            <Box className={classes.metricRow}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Agendado:
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {stats.totalAgendado}
              </Typography>
            </Box>
            <Box className={classes.metricRow}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Entregue:
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {stats.totalEntregue}
              </Typography>
            </Box>
            <Box className={classes.metricRow}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Respostas:
              </Typography>
              <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}>
                {stats.totalRespostas}
              </Typography>
            </Box>
            <Box className={classes.metricRow}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}>
                Taxa:
              </Typography>
              <Typography 
                variant="body2" 
                fontWeight={600} 
                color="primary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                {stats.taxaResposta}%
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid2>
  );

  if (loading) {
    return (
      <Box sx={{ width: "100%", mt: 2 }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <>
      <Typography className={classes.sectionTitle} sx={{ mt: 3, mb: 2 }}>
        Campanhas META
      </Typography>
      <Grid2 container spacing={3}>
        <MetricCard
          title="Geral"
          subtitle="Todas as campanhas"
          icon={<SendIcon />}
          bgColor="#1976d2"
          stats={stats.geral}
          templateName={null}
        />
        <MetricCard
          title="Preventivo"
          subtitle="preventivo_boleto"
          icon={<CheckCircleIcon />}
          bgColor="#2e7d32"
          stats={stats.preventivo}
          templateName="preventivo_boleto"
        />
        <MetricCard
          title="Preventivo 3 dias"
          subtitle="preventivo_3d"
          icon={<MessageIcon />}
          bgColor="#ed6c02"
          stats={stats.preventivo3d}
          templateName="preventivo_3d"
        />
        <MetricCard
          title="Quebra Acordo"
          subtitle="quebra_acordo"
          icon={<SendIcon />}
          bgColor="#d32f2f"
          stats={stats.quebraAcordo}
          templateName="quebra_acordo"
        />
        <MetricCard
          title="Cadastros Novos"
          subtitle="cadastro_novo"
          icon={<CheckCircleIcon />}
          bgColor="#9c27b0"
          stats={stats.cadastroNovo}
          templateName="cadastro_novo"
        />
      </Grid2>
    </>
  );
};

export default CampaignMetaMetrics;
